import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { testimonials } from "../config/testimonials";

const TestimonialCard = ({ testimonial, index }) => {
  return (
    <div
      className="flex-shrink-0 w-[340px] md:w-[400px] bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 mx-3"
      data-testid={`testimonial-card-${index}`}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-[#a5dae0] flex items-center justify-center flex-shrink-0">
          <span className="text-[#444444] font-bold text-lg">
            {testimonial.name.charAt(0)}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold truncate">{testimonial.name}</p>
          <p className="text-white/50 text-sm">{testimonial.timeAgo}</p>
        </div>
      </div>
      
      <div className="flex gap-1 text-[#a5dae0] mb-4">
        {[...Array(testimonial.rating)].map((_, j) => (
          <Star key={j} size={16} fill="currentColor" />
        ))}
      </div>
      
      <div className="relative">
        <Quote className="absolute -top-2 -left-1 text-[#a5dae0]/20" size={24} />
        <p className="text-white/90 leading-relaxed text-sm md:text-base pl-4">
          {testimonial.text}
        </p>
      </div>
    </div>
  );
};

const Testimonials = () => {
  const scrollRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const animationRef = useRef(null);
  const scrollPositionRef = useRef(0);

  // Duplicate testimonials for seamless infinite scroll
  const duplicatedTestimonials = [...testimonials, ...testimonials];

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const scrollSpeed = 0.5; // pixels per frame
    const totalWidth = container.scrollWidth / 2; // Half because we duplicated

    const animate = () => {
      if (!isPaused && container) {
        scrollPositionRef.current += scrollSpeed;
        
        // Reset to start when we've scrolled through the first set
        if (scrollPositionRef.current >= totalWidth) {
          scrollPositionRef.current = 0;
        }
        
        container.scrollLeft = scrollPositionRef.current;
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPaused]);

  return (
    <section className="py-20 md:py-32 bg-[#444444] overflow-hidden" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <p className="text-[#a5dae0] font-script text-2xl mb-4">Testimonials</p>
          <h2 className="text-4xl md:text-5xl text-white mb-4">What Our Clients Say</h2>
          <p className="text-white/60 text-sm md:text-base max-w-2xl mx-auto">
            Don't just take our word for it — hear from couples, party hosts, and event planners who trusted us with their special moments.
          </p>
        </motion.div>
      </div>

      {/* Carousel Container */}
      <div
        ref={scrollRef}
        className="flex overflow-x-hidden cursor-grab py-4"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={() => setIsPaused(true)}
        onTouchEnd={() => setIsPaused(false)}
        data-testid="testimonials-carousel"
      >
        {duplicatedTestimonials.map((testimonial, index) => (
          <TestimonialCard
            key={`${testimonial.name}-${index}`}
            testimonial={testimonial}
            index={index}
          />
        ))}
      </div>

      {/* Google Reviews Badge */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="flex justify-center mt-10"
      >
        <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3">
          <div className="flex gap-0.5 text-[#a5dae0]">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={16} fill="currentColor" />
            ))}
          </div>
          <span className="text-white/80 text-sm">
            5.0 rating with over {testimonials.length} reviews on Google
          </span>
        </div>
      </motion.div>
    </section>
  );
};

export default Testimonials;
